import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  createIntegration,
  getIntegrations,
  getNotificationContacts,
  getTenants,
  testWatiIntegration,
  updateIntegration,
  updateNotificationContacts,
} from '../services/tenant.service';
import { useAuthStore } from '../store/authStore';
import ScaleGate from '../components/common/ScaleGate';
import { phoneError } from '../utils/phone';
import PhoneField from '../components/common/PhoneField';
import WhatsAppAgentCard from '../components/whatsapp/WhatsAppAgentCard';
import InstagramAgentCard from '../components/instagram/InstagramAgentCard';

const DEFAULT_HANDOFF_LABEL = 'Chat with Our Team on WhatsApp';
const BSP_PROVIDERS = ['wati', 'gallabox', 'interakt', 'gupshup', 'aisensy', 'twilio', 'meta', 'custom'];

// WATI outbound integration constants — must match the backend service
// (src/services/wati_contact_push_service.py). Keep in sync.
const WATI_INTEGRATION_TYPE = 'messaging';
const WATI_PROVIDER = 'wati_contact_push';
const WATI_BASE_HOST = 'https://live-mt-server.wati.io';

function generateSecret() {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function getApiBaseUrl() {
  if (process.env.REACT_APP_API_URL) {
    return `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/v1`;
  }
  return 'https://<your-serviti-api-host>/api/v1';
}

export default function WhatsAppOutboundSettingsPage() {
  const { tenantId: urlTenantId } = useParams();
  const user = useAuthStore((s) => s.user);
  const isSuperadmin = user?.is_superuser || user?.role === 'superadmin';

  const [tenantList, setTenantList] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(urlTenantId || (!isSuperadmin ? user?.tenant_id : ''));
  const tenantId = selectedTenantId;

  const [loading, setLoading] = useState(true);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [widgetWhatsappHandoffEnabled, setWidgetWhatsappHandoffEnabled] = useState(true);
  const [whatsappHandoffNumber, setWhatsappHandoffNumber] = useState('');
  const [whatsappHandoffLabel, setWhatsappHandoffLabel] = useState(DEFAULT_HANDOFF_LABEL);
  const [bspProvider, setBspProvider] = useState('wati');
  const [bspWebhookSecret, setBspWebhookSecret] = useState('');
  const [savedBspWebhookSecret, setSavedBspWebhookSecret] = useState('');
  const [testingWebhook, setTestingWebhook] = useState(false);
  // Phase 2 (inbound WATI): when true, a cold WhatsApp message from a
  // never-seen phone auto-creates a Lead in Serviti, wires it to the
  // tenant's default agent, and fires the same alert fan-out as widget
  // captures. Stored under notification_preferences.
  const [autoCreateLeadsFromBsp, setAutoCreateLeadsFromBsp] = useState(false);

  // WATI outbound (contact push) — separate TenantIntegration row.
  // watiTokenSet = "we know a token is stored on the server, but we never
  // fetch the plaintext back down"; token input stays blank until the user
  // types a new value (only then does watiTokenDirty flip and we send it).
  const [watiIntegrationId, setWatiIntegrationId] = useState(null);
  const [watiEnabled, setWatiEnabled] = useState(false);
  const [watiTenantIdField, setWatiTenantIdField] = useState('');
  const [watiToken, setWatiToken] = useState('');
  const [watiTokenSet, setWatiTokenSet] = useState(false);
  const [watiTokenDirty, setWatiTokenDirty] = useState(false);
  const [watiTokenVisible, setWatiTokenVisible] = useState(false);
  const [watiSaving, setWatiSaving] = useState(false);
  const [watiTesting, setWatiTesting] = useState(false);

  useEffect(() => {
    if (!isSuperadmin) return;
    const loadTenants = async () => {
      try {
        setTenantsLoading(true);
        const data = await getTenants(true);
        setTenantList(data);
        if (!selectedTenantId && data.length > 0) {
          setSelectedTenantId(data[0].id);
        }
      } catch (err) {
        console.error('Failed to load tenants:', err);
        toast.error('Failed to load tenants');
      } finally {
        setTenantsLoading(false);
      }
    };
    loadTenants();
  }, [isSuperadmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSettings = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await getNotificationContacts(tenantId);
      const prefs = data.notification_preferences || {};
      setWidgetWhatsappHandoffEnabled(prefs.widget_whatsapp_handoff_enabled !== false);
      setWhatsappHandoffNumber(prefs.whatsapp_handoff_number || prefs.sales_whatsapp_number || '');
      setWhatsappHandoffLabel(prefs.whatsapp_handoff_label || prefs.sales_whatsapp_label || DEFAULT_HANDOFF_LABEL);
      setBspProvider(prefs.bsp_webhook_provider || 'wati');
      setBspWebhookSecret(prefs.bsp_webhook_secret || '');
      setSavedBspWebhookSecret(prefs.bsp_webhook_secret || '');
      setAutoCreateLeadsFromBsp(Boolean(prefs.auto_create_leads_from_bsp_inbound));

      // WATI outbound integration — separate integrations row. Backend
      // response redacts the bearer_token to "••••••" via
      // TenantIntegration._redact_config, so we never see the plaintext.
      try {
        const integrations = await getIntegrations(tenantId, WATI_INTEGRATION_TYPE);
        const wati = (integrations || []).find(
          (row) => row.provider === WATI_PROVIDER
        );
        if (wati) {
          setWatiIntegrationId(wati.id);
          setWatiEnabled(Boolean(wati.is_active));
          const cfg = wati.config || {};
          setWatiTenantIdField(cfg.wati_tenant_id || '');
          setWatiTokenSet(Boolean(cfg.bearer_token));
          setWatiToken('');
          setWatiTokenDirty(false);
        } else {
          setWatiIntegrationId(null);
          setWatiEnabled(false);
          setWatiTenantIdField('');
          setWatiTokenSet(false);
          setWatiToken('');
          setWatiTokenDirty(false);
        }
      } catch (watiErr) {
        // Non-fatal — the rest of the page still works. Log and continue.
        console.warn('Failed to load WATI integration:', watiErr);
      }
    } catch (err) {
      console.error('Failed to load WhatsApp/BSP settings:', err);
      toast.error('Failed to load WhatsApp/BSP settings');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Optional, but a handoff number without a country code cannot be dialled
  // from the widget's WhatsApp deep link.
  const handoffNumberError = whatsappHandoffNumber.trim()
    ? phoneError(whatsappHandoffNumber)
    : null;

  const handleSave = async () => {
    if (!tenantId) return;
    const cleanHandoffNumber = whatsappHandoffNumber.trim();
    if (handoffNumberError) {
      toast.error(handoffNumberError);
      return;
    }

    try {
      setSaving(true);
      const response = await updateNotificationContacts(tenantId, {
        notification_preferences: {
          widget_whatsapp_handoff_enabled: widgetWhatsappHandoffEnabled,
          whatsapp_handoff_number: cleanHandoffNumber,
          whatsapp_handoff_label: whatsappHandoffLabel.trim() || DEFAULT_HANDOFF_LABEL,
          bsp_webhook_provider: bspProvider,
          bsp_webhook_secret: bspWebhookSecret.trim(),
          auto_create_leads_from_bsp_inbound: autoCreateLeadsFromBsp,
        },
      });
      setSavedBspWebhookSecret(bspWebhookSecret.trim());
      toast.success(response.message || 'WhatsApp/BSP settings saved');
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(err?.response?.data?.detail || 'Failed to save WhatsApp/BSP settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || tenantsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const selectedTenantName = tenantList.find(t => t.id === tenantId)?.name;
  const cleanSecret = bspWebhookSecret.trim();
  const webhookUrl = tenantId
    ? `${getApiBaseUrl()}/webhooks/bsp/${encodeURIComponent(bspProvider)}/${tenantId}${cleanSecret ? `?secret=${encodeURIComponent(cleanSecret)}` : ''}`
    : '';

  const copyText = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error('Copy failed');
    }
  };

  const rotateBspWebhookSecret = () => {
    if (savedBspWebhookSecret) {
      const confirmed = window.confirm(
        'Rotating this webhook secret will stop the current WATI/BSP webhook after you save. ' +
        'Update the webhook URL or X-Serviti-BSP-Secret in your BSP before relying on capture. Continue?'
      );
      if (!confirmed) return;
    }
    setBspWebhookSecret(generateSecret());
  };

  // WATI outbound helpers ----------------------------------------------------
  // Build the WATI API base URL from the numeric tenant ID the user types.
  // Kept as a derived value rather than a form field so the customer can't
  // paste the wrong host and silently talk to a stranger's WATI shard.
  const watiTenantIdClean = watiTenantIdField.trim();
  const watiBaseUrl = watiTenantIdClean
    ? `${WATI_BASE_HOST}/${watiTenantIdClean}`
    : '';

  const handleWatiTest = async () => {
    if (!tenantId) return;
    if (!watiTenantIdClean) {
      toast.error('Enter your WATI tenant ID first.');
      return;
    }
    setWatiTesting(true);
    try {
      const payload = {
        base_url: watiBaseUrl,
      };
      // Only send the token if the user has typed a new value in this session.
      // Otherwise we let the backend fall back to the stored one.
      if (watiTokenDirty && watiToken.trim()) {
        payload.bearer_token = watiToken.trim();
      } else if (!watiIntegrationId) {
        // Nothing saved yet AND nothing typed — nothing to test.
        toast.error('Enter your WATI bearer token first.');
        setWatiTesting(false);
        return;
      }
      const result = await testWatiIntegration(tenantId, payload);
      if (result.success) {
        toast.success(result.message || 'WATI connection successful');
      } else {
        toast.error(result.message || 'WATI test failed');
      }
    } catch (err) {
      console.error('WATI test failed:', err);
      toast.error(err?.response?.data?.detail || 'WATI test failed');
    } finally {
      setWatiTesting(false);
    }
  };

  const handleWatiSave = async () => {
    if (!tenantId) return;
    if (!watiTenantIdClean) {
      toast.error('WATI tenant ID is required.');
      return;
    }
    if (!watiIntegrationId && !watiToken.trim()) {
      toast.error('WATI bearer token is required on first save.');
      return;
    }

    setWatiSaving(true);
    try {
      // Only include the bearer_token in config if the user actually typed
      // one — otherwise we'd blank the stored value on every save.
      // No default_country_code. This screen has no field for one, so the
      // only value it could ever write was a hard-coded '91' — and the server
      // trusts an explicit config value ahead of everything else, which made a
      // UAE or US tenant's bare ten-digit leads get rewritten as Indian numbers
      // the moment they saved WATI settings. With the key absent the server
      // derives it from the tenant's own validated business phone, and refuses
      // the ambiguous number when it cannot.
      const config = {
        wati_tenant_id: watiTenantIdClean,
      };
      if (watiTokenDirty && watiToken.trim()) {
        config.bearer_token = watiToken.trim();
      }

      if (watiIntegrationId) {
        await updateIntegration(tenantId, watiIntegrationId, {
          base_url: watiBaseUrl,
          config,
          is_active: watiEnabled,
        });
      } else {
        const created = await createIntegration(tenantId, {
          integration_type: WATI_INTEGRATION_TYPE,
          provider: WATI_PROVIDER,
          display_name: 'WATI Contact Push',
          base_url: watiBaseUrl,
          config,
          is_active: watiEnabled,
        });
        setWatiIntegrationId(created?.id || null);
      }
      // If we sent a token, the server now knows about it — reset the dirty
      // flag and mark "a token is set" so subsequent saves don't require it.
      if (watiTokenDirty && watiToken.trim()) {
        setWatiTokenSet(true);
        setWatiTokenDirty(false);
        setWatiToken('');
        setWatiTokenVisible(false);
      }
      toast.success('WATI outbound settings saved');
    } catch (err) {
      console.error('WATI save failed:', err);
      toast.error(err?.response?.data?.detail || 'WATI save failed');
    } finally {
      setWatiSaving(false);
    }
  };

  const testWebhook = async () => {
    if (!webhookUrl) {
      toast.error('Enter a secret first');
      return;
    }
    setTestingWebhook(true);
    try {
      // POST the exact public URL the BSP would hit. Tests the full path:
      // URL routing, tenant lookup, secret check. Backend short-circuits on
      // _serviti_test and persists nothing.
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ _serviti_test: true }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.test_received) {
        toast.success('Webhook reachable, secret accepted');
      } else if (response.status === 403) {
        toast.error('Webhook reached, but secret was rejected. Save first?');
      } else if (response.status === 404) {
        toast.error('Tenant not found at that URL');
      } else if (response.status === 400) {
        toast.error(data.detail || 'BSP webhook secret is not configured (save first)');
      } else {
        toast.error(`Webhook test failed: HTTP ${response.status}`);
      }
    } catch (err) {
      console.error('Webhook test failed:', err);
      toast.error(`Could not reach webhook: ${err.message}`);
    } finally {
      setTestingWebhook(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8">
      <div className="flex items-center gap-3">
        <ChatBubbleLeftRightIcon className="h-8 w-8 text-indigo-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Channels</h1>
          <p className="text-sm text-gray-500">
            Choose where your AI agent answers customers. Team alert numbers are managed separately in Lead alerts and Support alerts.
          </p>
        </div>
      </div>

      {isSuperadmin && (
        <div className="rounded-lg bg-white border border-gray-200 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <BuildingOfficeIcon className="h-5 w-5 text-gray-500" />
            <label htmlFor="tenant-select" className="text-sm font-semibold text-gray-700">
              Select Tenant
            </label>
          </div>
          <select
            id="tenant-select"
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-3 py-2 border"
          >
            <option value="">-- Select a tenant --</option>
            {tenantList.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>
        </div>
      )}

      {!tenantId ? (
        <div className="text-center py-12 text-gray-400">
          <BuildingOfficeIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium">Select a workspace above to manage its channels.</p>
        </div>
      ) : (
        <>
          <details open className="rounded-lg border border-gray-200 bg-white p-4">
            <summary className="min-h-[44px] cursor-pointer text-lg font-semibold">WhatsApp — answer customers</summary>
            <WhatsAppAgentCard key={tenantId} tenantId={tenantId} isSuperadmin={isSuperadmin} />
          </details>
          <details className="rounded-lg border border-gray-200 bg-white p-4">
            <summary className="min-h-[44px] cursor-pointer text-lg font-semibold">Instagram — answer direct messages</summary>
            <InstagramAgentCard key={tenantId} tenantId={tenantId} isSuperadmin={isSuperadmin} />
          </details>

          {/*
            Page-level primer replacing two older banners that had drifted
            out of date once BSP Conversation Capture and WATI Contact Push
            landed on the same page. Keeps the "personal number ≠ two-way
            sync" caveat that was buried in the old amber banner without
            implying full sync is a future feature.
          */}
          <section className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex gap-3">
              <InformationCircleIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
              <p className="text-sm text-blue-900">
                The options below are optional. A website WhatsApp button opens WhatsApp; it does not connect an AI agent.
                Existing-provider integrations are only needed if you already use WATI or a similar service.
              </p>
            </div>
          </section>

          <details className="rounded-lg border border-gray-200 bg-white p-4">
          <summary className="min-h-[44px] cursor-pointer font-semibold">Optional: website WhatsApp button</summary>
          <ScaleGate feature="WhatsApp Human Handoff" minTier="growth">
          <section className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-5 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">Website WhatsApp button</h2>
                <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">Growth</span>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Show a chat button in the AI widget so visitors can move from the AI agent to your team on WhatsApp.
              </p>
            </div>

            <div className="p-5 space-y-5">
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={widgetWhatsappHandoffEnabled}
                  onChange={(e) => setWidgetWhatsappHandoffEnabled(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <span>
                  <span className="font-medium text-gray-900">Show WhatsApp handoff button in the widget</span>
                  <span className="block text-xs text-gray-500">
                    If disabled, the button stays hidden even when a number is configured.
                  </span>
                </span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="whatsapp-handoff-number" className="block text-sm font-medium text-gray-700 mb-1">
                    Number visitors contact
                  </label>
                  <PhoneField
                    id="whatsapp-handoff-number"
                    value={whatsappHandoffNumber}
                    onChange={setWhatsappHandoffNumber}
                    selectClassName="w-36 shrink-0 rounded-md border border-gray-300 bg-white px-2 py-2 text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    inputClassName={`min-w-0 flex-1 rounded-md shadow-sm text-sm px-3 py-2 border ${
                      handoffNumberError
                        ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                    }`}
                  />
                  {handoffNumberError && (
                    <p className="text-xs text-red-500 mt-1">{handoffNumberError}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Include country code. If blank, the WhatsApp handoff button stays hidden.
                  </p>
                </div>
                <div>
                  <label htmlFor="whatsapp-handoff-label" className="block text-sm font-medium text-gray-700 mb-1">
                    Button Label
                  </label>
                  <input
                    id="whatsapp-handoff-label"
                    type="text"
                    value={whatsappHandoffLabel}
                    onChange={(e) => setWhatsappHandoffLabel(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-3 py-2 border"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Opens WhatsApp with recent chat context prefilled for the visitor to send.
                  </p>
                </div>
              </div>
            </div>
          </section>
          </ScaleGate>
          </details>

          <details className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <summary className="cursor-pointer select-none px-5 py-4 text-base font-semibold text-gray-900">
              Advanced: existing provider integrations (WATI or similar)
            </summary>
            <div className="space-y-4 px-5 pb-5">
              <div className="flex flex-wrap items-center gap-3 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <span>These settings are usually set up by whoever manages your WhatsApp provider.</span>
                <button
                  type="button"
                  onClick={() => copyText(window.location.href, 'Link to this page')}
                  className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                >
                  Copy link for your developer
                </button>
              </div>
          <ScaleGate feature="BSP Conversation Capture" entitlement="integrations" addonLabel="Integrations">
          <section className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-5 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">Bring provider chats into Serviti</h2>
                <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">Scale</span>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Copies WhatsApp messages from your provider into Serviti, so your team sees the whole conversation.
              </p>
            </div>

            <div className="p-5 space-y-4">
              {/*
                Phase 2 (inbound WATI auto-lead): when a cold WhatsApp
                message arrives from a phone Serviti has never seen, create
                a Lead, assign the tenant's default agent, and fire the
                usual alert fan-out. Dark-launched — default off.
              */}
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={autoCreateLeadsFromBsp}
                  onChange={(e) => setAutoCreateLeadsFromBsp(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <span>
                  <span className="font-medium text-gray-900">
                    Auto-create leads from cold inbound WhatsApp messages
                  </span>
                  <span className="block text-xs text-gray-500">
                    When someone WhatsApps your business number for the first time (via QR scan, ad click-through, or organic reach), Serviti creates a Lead using the WhatsApp profile name + first message, assigns your default agent, and fires the standard email + WhatsApp lead alerts. Same behaviour as web widget captures.
                  </span>
                </span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="bsp-provider" className="block text-sm font-medium text-gray-700 mb-1">
                    BSP Provider
                  </label>
                  <select
                    id="bsp-provider"
                    value={bspProvider}
                    onChange={(e) => setBspProvider(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-3 py-2 border"
                  >
                    {BSP_PROVIDERS.map((provider) => (
                      <option key={provider} value={provider}>
                        {provider.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="bsp-webhook-secret" className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook Secret
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="bsp-webhook-secret"
                      type="text"
                      value={bspWebhookSecret}
                      onChange={(e) => setBspWebhookSecret(e.target.value)}
                      placeholder="Generate a tenant-specific secret"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-3 py-2 border"
                    />
                    <button
                      type="button"
                      onClick={rotateBspWebhookSecret}
                      className="px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {savedBspWebhookSecret ? 'Rotate' : 'Generate'}
                    </button>
                  </div>
                  {savedBspWebhookSecret && bspWebhookSecret !== savedBspWebhookSecret && (
                    <p className="mt-1 text-xs text-amber-700">
                      New secret is not active yet. Save, then update the webhook secret in your BSP.
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="bsp-webhook-url" className="block text-sm font-medium text-gray-700 mb-1">
                  Webhook URL
                </label>
                <div className="flex gap-2">
                  <input
                    id="bsp-webhook-url"
                    type="text"
                    value={webhookUrl}
                    readOnly
                    className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm text-sm px-3 py-2 border"
                  />
                  <button
                    type="button"
                    disabled={!cleanSecret}
                    onClick={() => copyText(webhookUrl, 'Webhook URL')}
                    className="px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Copy
                  </button>
                  <button
                    type="button"
                    disabled={!cleanSecret || testingWebhook}
                    onClick={testWebhook}
                    className="px-3 py-2 rounded-md border border-indigo-300 text-sm text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                  >
                    {testingWebhook ? 'Testing…' : 'Test'}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Save after generating the secret, then paste this URL into your BSP webhook settings. If your BSP supports custom headers,
                  send the same secret as <span className="font-mono">X-Serviti-BSP-Secret</span> instead of the query string.
                  Use <span className="font-medium">Test</span> after saving to check the connection. The test message changes nothing.
                </p>
              </div>

              {/*
                Concrete WATI-side steps. Kept as an inline collapsible so
                power users can skim past once they've configured it, but
                first-time customers see the exact URL + field names to
                paste into. Uses a native <details> element — no extra
                dependency, keyboard-accessible, indexable by browser find.
              */}
              {bspProvider === 'wati' && (
                <details className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
                  <summary className="cursor-pointer font-medium text-gray-800">
                    How to add this webhook in WATI
                  </summary>
                  <ol className="mt-2 list-decimal pl-4 space-y-1.5">
                    <li>
                      Open your WATI dashboard at <span className="font-mono">https://live.wati.io/&#123;your-wati-tenant-id&#125;/webhooks</span>
                      &nbsp;(e.g. <span className="font-mono">live.wati.io/10193453/webhooks</span>). Your tenant ID is the numeric segment in every WATI dashboard URL.
                    </li>
                    <li>
                      Click <span className="font-medium">Add Webhook</span> (or the equivalent "Create webhook" button in newer WATI UIs).
                    </li>
                    <li>
                      Paste the <span className="font-medium">Webhook URL</span> above into WATI's URL field. The <span className="font-mono">?secret=&hellip;</span> query string travels with the URL — WATI will send it back on every event.
                    </li>
                    <li>
                      Subscribe to event types <span className="font-mono">message_received</span>, <span className="font-mono">message_sent</span>, and <span className="font-mono">contact_updated</span> (names vary slightly across WATI versions — subscribe to all inbound message-related events).
                    </li>
                    <li>
                      Save in WATI. The Test button checks Serviti's webhook URL and secret only. Send a real test message through WATI to confirm messages reach the Customer inbox.
                    </li>
                  </ol>
                </details>
              )}
            </div>
          </section>
          </ScaleGate>


          {/*
            WATI Outbound (Serviti → WATI contact push).
            Configured as a separate TenantIntegration row so it can be
            enabled/rotated/tested without touching notification_preferences.
            The Save button here is intentionally distinct from the main
            WhatsApp/BSP Save above — the two saves target different
            backend endpoints and shouldn't be entangled.
          */}
          <ScaleGate feature="WATI Contact Push" entitlement="integrations" addonLabel="Integrations">
          <section className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-5 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Sync captured leads to WATI
                </h2>
                <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">Scale</span>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                After Serviti captures a lead, push the AI conversation summary + score + campaign as custom contact attributes on the recipient's WATI contact. Your sales team sees the enrichment in the WATI Team Inbox contact panel without switching to Serviti.
              </p>
            </div>

            <div className="p-5 space-y-5">
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={watiEnabled}
                  onChange={(e) => setWatiEnabled(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <span>
                  <span className="font-medium text-gray-900">Push captured leads to WATI</span>
                  <span className="block text-xs text-gray-500">
                    When enabled, every new Serviti lead also updates that person&rsquo;s contact in WATI. Lead capture always completes even if WATI is unreachable — use <span className="font-medium">Test Connection</span> before saving to confirm the credentials work end-to-end.
                  </span>
                </span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="wati-tenant-id" className="block text-sm font-medium text-gray-700 mb-1">
                    WATI Tenant ID
                  </label>
                  <input
                    id="wati-tenant-id"
                    type="text"
                    inputMode="numeric"
                    placeholder="10193453"
                    value={watiTenantIdField}
                    onChange={(e) => setWatiTenantIdField(e.target.value.replace(/[^\d]/g, ''))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-3 py-2 border font-mono"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Numeric segment in every WATI dashboard URL — <span className="font-mono">live.wati.io/<b>10193453</b>/teamInbox</span>, <span className="font-mono">/api-docs</span>, <span className="font-mono">/webhooks</span> etc.
                  </p>
                </div>
                <div>
                  <label htmlFor="wati-bearer-token" className="block text-sm font-medium text-gray-700 mb-1">
                    WATI Bearer Token
                    {watiTokenSet && !watiTokenDirty && (
                      <span className="ml-2 text-xs text-emerald-600 font-normal">
                        (currently set — leave blank to keep)
                      </span>
                    )}
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="wati-bearer-token"
                      type={watiTokenVisible ? 'text' : 'password'}
                      value={watiToken}
                      onChange={(e) => {
                        setWatiToken(e.target.value);
                        setWatiTokenDirty(true);
                      }}
                      placeholder={watiTokenSet ? '••••••••••••••••' : 'wati_...'}
                      autoComplete="off"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-3 py-2 border font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setWatiTokenVisible((v) => !v)}
                      className="px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {watiTokenVisible ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Open <span className="font-mono">https://live.wati.io/&#123;your-wati-tenant-id&#125;/api-docs</span> → <span className="font-medium">Create API Token</span>. Scopes needed: <span className="font-mono">contacts:read</span>, <span className="font-mono">contacts:write</span>. Copy the token that starts with <span className="font-mono">wati_&hellip;</span> — paste it here (not the JWT shown on the API Docs page; that&rsquo;s the legacy V1 token).
                  </p>
                </div>
              </div>

              {watiTenantIdClean && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Resolved WATI API Base URL
                  </label>
                  <input
                    type="text"
                    value={watiBaseUrl}
                    readOnly
                    className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm text-sm px-3 py-2 border font-mono"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Serviti will POST to <span className="font-mono">{watiBaseUrl}/api/v1/updateContactAttributes/&#123;phone&#125;</span> for every captured lead.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleWatiTest}
                  disabled={watiTesting || !watiTenantIdClean || (!watiIntegrationId && !watiToken.trim())}
                  className="px-4 py-2 rounded-md border border-indigo-300 text-sm text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                >
                  {watiTesting ? 'Testing…' : 'Test Connection'}
                </button>
                <button
                  type="button"
                  onClick={handleWatiSave}
                  disabled={watiSaving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition"
                >
                  {watiSaving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                  Save WATI contact sync
                </button>
              </div>
            </div>
          </section>
          </ScaleGate>
            </div>
          </details>

          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving || !!handoffNumberError}
              title={handoffNumberError || undefined}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
              Save website button and provider settings
            </button>
          </div>

          {selectedTenantName && (
            <p className="text-xs text-gray-400 text-right">
              Editing {selectedTenantName}
            </p>
          )}
        </>
      )}
    </div>
  );
}
